import { useEffect, useState } from "react";
import { Loader2, ScanBarcode } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { api, errMsg, imgUrl, UNIT_LABELS } from "@/lib/api";

const STATUS = {
  loading: ["text-muted-foreground", "Buscando o produto…"],
  found: ["text-emerald-600 dark:text-emerald-400", "Encontrado na Open Food Facts. Confira o nome e informe o preço."],
  notfound: ["text-muted-foreground", "Produto não encontrado. Preencha os dados manualmente."],
};
const selCls = "h-10 rounded-md border bg-transparent px-2 text-sm";

export function QuickProductDialog({ code, categories, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", price: "", category_id: "", unit: "un", description: "", image_url: "" });
  const [status, setStatus] = useState("loading");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  useEffect(() => {
    api.get(`/products/barcode/${code}`).then(({ data }) => {
      if (data.duplicate) { toast.error(`Este código já é do produto ${data.duplicate.name} (inativo). Ative-o em Produtos.`); onClose(); return; }
      if (data.found) setF((x) => ({ ...x, name: data.name, description: data.description, image_url: data.image_url }));
      setStatus(data.found ? "found" : "notfound");
    }).catch(() => setStatus("notfound"));
  }, [code, onClose]);
  const ok = f.name.trim() && Number(f.price) > 0 && f.category_id;
  const save = async () => {
    if (!ok || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/products", { name: f.name.trim(), barcode: code, price: Number(f.price), category_id: f.category_id === "geral" ? null : f.category_id, unit: f.unit, description: f.description, image_url: f.image_url });
      toast.success(`${data.name} cadastrado e adicionado à venda`);
      onCreated(data);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const [tone, text] = STATUS[status];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="quick-product-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanBarcode className="h-5 w-5 text-primary" />Cadastrar produto</DialogTitle></DialogHeader>
        <p className="text-sm">O código <span className="font-mono font-semibold" data-testid="quick-product-code">{code}</span> ainda não está cadastrado.</p>
        <p className={`flex items-center gap-2 text-xs ${tone}`} data-testid="quick-product-status">{status === "loading" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{text}</p>
        <div className="flex gap-4">
          {f.image_url && <img src={imgUrl(f.image_url)} alt="" className="h-24 w-24 shrink-0 rounded-lg border object-contain bg-white" data-testid="quick-product-image" />}
          <div className="grid flex-1 grid-cols-2 gap-3">
            <Field label="Nome" className="col-span-2"><Input value={f.name} onChange={set("name")} disabled={status === "loading"} data-testid="quick-product-name" /></Field>
            <Field label="Preço de venda (R$)"><Input type="number" step="0.01" min="0" value={f.price} onChange={set("price")} autoFocus onKeyDown={(e) => e.key === "Enter" && save()} data-testid="quick-product-price" /></Field>
            <Field label="Unidade"><select value={f.unit} onChange={set("unit")} className={selCls} data-testid="quick-product-unit">{Object.entries(UNIT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
            <Field label="Categoria *" className="col-span-2"><select value={f.category_id} onChange={set("category_id")} className={selCls} data-testid="quick-product-category"><option value="" disabled>Escolha a categoria</option><option value="geral">Geral</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="quick-product-cancel">Cancelar</Button>
          <Button onClick={save} disabled={!ok || busy || status === "loading"} data-testid="quick-product-save">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Cadastrar e adicionar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
